import * as api from '@apis/program-schedule';
import SelectProductModal from '@components/product/SelectProductModal';
import { SelectUser } from '@components/product/SelectUser';
import { useAppSocket } from '@hooks/appSocket';
import { useAuthentication } from '@hooks/authentication';
import { ProductModel } from '@models/product';
import { GetProgramSchedule, PaymentStatementScheduleStatus, ProgramSchedule } from '@models/program-schedule';
import { ProductOrderViewModel } from '@models/productOrderView';
import { useQuery } from '@tanstack/react-query';
import { getUniqueKeyFromUUID } from '@utils/string';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Popover, Table } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '@utils/dataTransform';

const REFUND_STATUSES: { [key: string]: any } = {
  all: (
    <div className="badge" style={{ backgroundColor: '#bd334a' }}>
      전체 환불
    </div>
  ),
  partial: (
    <div className="badge" style={{ backgroundColor: '#d944db' }}>
      부분 환불
    </div>
  ),
  requested: (
    <div className="badge" style={{ backgroundColor: '#db9737' }}>
      환불 요청
    </div>
  ),
  accepted: (
    <div className="badge" style={{ backgroundColor: '#2782b3' }}>
      환불 예정
    </div>
  ),
  denied: (
    <div className="badge" style={{ backgroundColor: '#6e6e6e' }}>
      환불 반려
    </div>
  ),
  reserved: (
    <div className="badge" style={{ backgroundColor: 'green' }}>
      환불 예약
    </div>
  ),
  request_cancelled: (
    <div className="badge" style={{ backgroundColor: 'tomato' }}>
      환불 철회
    </div>
  ),
  submission: (
    <div className="badge" style={{ backgroundColor: 'pink' }}>
      환불 접수
    </div>
  ),
  none: '-',
};

const SCHEDULE_STATUS_STYLES: { [key: string]: { label: string; backgroundColor: string; color: string } } = {
  ready: { label: '대기중', backgroundColor: '#bdd6fc', color: '#242424' },
  confirm: { label: '확정', backgroundColor: '#23b823', color: 'white' },
  cancel: { label: '취소', backgroundColor: '#f2559e', color: 'white' },
  completed: { label: '완료', backgroundColor: '#444744', color: 'white' },
};

const PaymentStatementDisplayModal: React.FC<{
  open: boolean;
  onClose: () => void;
  productOrder: ProductOrderViewModel | null;
}> = ({ open, onClose, productOrder }) => {
  if (!productOrder) return null;

  return (
    <Modal title="주문내역" open={open} onCancel={onClose} footer={null} width={800}>
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h3>주문 정보</h3>
          <p>주문번호: {productOrder._id}</p>
          <p>주문일시: {dayjs(productOrder.createdAt).format('YYYY-MM-DD HH:mm')}</p>
          <p>주문상태: {productOrder.statuses?.payment}</p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3>결제 정보</h3>
          <p>결제금액: {productOrder.payment.amount.toLocaleString()}원</p>
          <p>결제수단: {productOrder.payment.method}</p>
          <p>결제상태: {productOrder.statuses.payment}</p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3>주문자 정보</h3>
          <p>이름: {productOrder.buyer.name}</p>
          <p>전화번호: {productOrder.buyer.phone}</p>
        </div>

        <div>
          <h3>상품 정보</h3>
          <div style={{ marginBottom: '10px', padding: '10px', border: '1px solid #eee' }}>
            <p>상품명: {productOrder.product.name}</p>
            <p>가격: {productOrder.payment.amount.toLocaleString()}원</p>
          </div>
        </div>
        <div>
          <h3>구매 정보</h3>
          {productOrder.purchases?.map((purchase, index) => (
            <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #eee' }}>
              <p>옵션: {purchase.name}</p>
              <p>수량: {purchase.count}</p>
              <p>옵션가격: {purchase.fee.toLocaleString()}원</p>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

const ScheduleCountByOptionModal: React.FC<{
  open: boolean;
  onClose: () => void;
  data: { _id: { optionName: string; productName: string; partnerId: string; partnerName: string }; count: number }[];
}> = ({ open, onClose, data }) => {
  return (
    <Modal title="옵션별 인원 수" open={open} onCancel={onClose} footer={null} width={1024}>
      <Table
        columns={[
          {
            title: '파트너명',
            dataIndex: ['_id', 'partnerName'],
            key: 'partnerName',
          },
          {
            title: '상품명',
            dataIndex: ['_id', 'productName'],
            key: 'productName',
            sorter: { compare: (a, b) => a._id.productName.localeCompare(b._id.productName), multiple: 3 },
            filterMultiple: true,
          },
          {
            title: '옵션명',
            dataIndex: ['_id', 'optionName'],
            key: 'optionName',
            sorter: { compare: (a, b) => a._id.optionName.localeCompare(b._id.optionName), multiple: 2 },
          },
          {
            title: '인원 수',
            width: 100,
            dataIndex: 'count',
            key: 'count',
            align: 'right',
            sorter: { compare: (a, b) => a.count - b.count, multiple: 1 },
          },
        ]}
        dataSource={data}
        pagination={false}
      />
    </Modal>
  );
};

export const ProgramSchedulesPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [startDate, setStartDate] = useState<Date>(dayjs().toDate());
  const [endDate, setEndDate] = useState<Date>(dayjs().add(1, 'week').endOf('day').toDate());
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isProductSelectModalVisible, setIsProductSelectModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductModel>();
  const [selectedPartner, setSelectedPartner] = useState<any>();
  const [form] = Form.useForm();
  const searchInputRef = useRef<any>();
  const appSocket = useAppSocket();
  const [isPaymentStatementModalOpen, setIsPaymentStatementModalOpen] = useState(false);
  const [selectedProductOrder, setSelectedProductOrder] = useState<ProductOrderViewModel | null>(null);
  const [isScheduleCountModalVisible, setIsScheduleCountModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useAuthentication();

  const programSchedulesQuery = useQuery({
    queryKey: ['program-schedules', startDate, endDate, currentPage, pageSize],
    queryFn: () =>
      api.searchProgramSchedules({
        keyword: searchKeyword,
        startDate,
        endDate,
        page: currentPage - 1,
        size: pageSize,
      }),
  });

  const programSchedulesCountByOptionNameQuery = useQuery({
    queryKey: ['program-schedules-count-by-option-name', startDate, endDate],
    queryFn: () => api.getProgramSchedulesCountByOptionName({ startDate, endDate }),
  });

  useEffect(() => {
    appSocket.socket.addSocketEventListener('upload_excel_status', 'upload_excel_status', (data) => {
      if (data.status === 'success') {
        programSchedulesQuery.refetch();
        toast.success('일정이 업데이트되었습니다', {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      }
    });
  }, []);

  window.onbeforeunload = () => {
    appSocket.socket.removeSocketEventListener('upload_excel_status', 'upload_excel_status');
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const startDate = values.startDate;
      const endDate = startDate.add(values.duration, 'minute');
      const week = startDate.day(); // Get day of week (0-6)

      const scheduleData: Partial<ProgramSchedule> = {
        sourceName: '외부 접수',
        partnerId: selectedPartner?._id,
        partnerName: selectedPartner?.name,
        product: { id: selectedProduct?._id || '', name: selectedProduct?.title || '' },
        representative: {
          name: values.representativeName,
          phone: values.representativePhone,
        },
        schedule: {
          id: getUniqueKeyFromUUID(),
          scheduleName: values.scheduleName,
          startDate: startDate.toDate(),
          endDate: endDate.toDate(),
          localDateString: startDate.format('YYYY-MM-DD'),
          week: week,
          duration: values.duration,
          scheduleInfo: {
            name: values.participantName,
            birthYear: values.birthYear,
          },
          inputOption: values.inputOption,
          status: PaymentStatementScheduleStatus.READY,
          optionName: values.optionName,
        },
      };

      await api.createProgramSchedule(scheduleData);
      toast.success('외부 프로그램 일정이 생성되었습니다');
      setIsCreateModalVisible(false);
      form.resetFields();
      setSelectedProduct(undefined);
      setSelectedPartner(undefined);
      programSchedulesQuery.refetch();
    } catch (error) {
      console.error('Error creating program schedule:', error);
      toast.error('일정 생성 중 오류가 발생했습니다');
    }
  };

  const TABLE_COLUMNS: ColumnsType<GetProgramSchedule> = [
    {
      title: 'ID',
      dataIndex: '_id',
      align: 'center',
      width: 100,
      render: (id) => {
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(id);
              toast.dark('일정 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
        );
      },
    },
    {
      title: '소스',
      align: 'center',
      width: 80,
      dataIndex: ['sourceName'],
      render: (sourceName) => {
        return <div>{sourceName}</div>;
      },
    },
    {
      title: '상품명',
      dataIndex: ['product', 'name'],
      width: 200,
      render: (name) => {
        return (
          <Popover content={name}>
            <div style={{ width: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </div>
          </Popover>
        );
      },
    },
    {
      title: '일정명',
      dataIndex: ['schedule'],
      width: 200,
      render: (schedule) => {
        return (
          <Popover content={schedule.optionName}>
            <div style={{ width: '200px', whiteSpace: 'wrap' }}>{schedule.scheduleName}</div>
          </Popover>
        );
      },
    },
    {
      title: '대표자',
      align: 'center',
      width: 140,
      dataIndex: ['representative', 'name'],
      render: (name, record) => {
        return (
          <div>
            <div>{name}</div>
            <div>{formatPhoneNumber(record.representative?.phone)}</div>
          </div>
        );
      },
    },
    {
      title: '참여자',
      align: 'center',
      width: 80,
      dataIndex: ['schedule', 'scheduleInfo'],
      render: (scheduleInfo) => {
        return <div>{scheduleInfo?.name}</div>;
      },
    },
    {
      title: '생년/학년',
      align: 'center',
      width: 120,
      dataIndex: ['schedule', 'scheduleInfo'],
      render: (scheduleInfo) => {
        const birthYear = scheduleInfo?.birthYear;
        let schoolYear = '';
        if (birthYear) {
          const age = new Date().getFullYear() - parseInt(birthYear) + 1;
          if (age >= 8 && age <= 19) {
            const grade = age - 7;
            if (grade <= 6) {
              schoolYear = `${grade}학년(초)`;
            } else if (grade <= 9) {
              schoolYear = `${grade - 6}학년(중)`;
            } else {
              schoolYear = `${grade - 9}학년(고)`;
            }
          }
        }
        return (
          <div>
            <div>{birthYear}</div>
            <div>{schoolYear}</div>
          </div>
        );
      },
    },
    {
      title: '친구들',
      width: 120,
      dataIndex: ['schedule', 'scheduleInfo'],
      render: (scheduleInfo) => {
        return <div>{scheduleInfo?.friends}</div>;
      },
    },
    {
      title: '옵션',
      width: 140,
      dataIndex: ['additionalPurchaseOptions'],
      render: (additionalPurchaseOptions) => {
        return (
          <div>
            {(additionalPurchaseOptions || []).map((purchase: any) => `${purchase.name}(${purchase.count})`).join('\n')}
          </div>
        );
      },
    },
    {
      title: '비고',
      width: 140,
      dataIndex: ['schedule', 'notes'],
      render: (notes) => {
        return <div>{notes}</div>;
      },
    },
    {
      title: '상태',
      width: 100,
      dataIndex: ['schedule'],
      align: 'center',
      render: (schedule) => {
        const status = schedule.status;
        const endDate = schedule.endDate;
        if (!SCHEDULE_STATUS_STYLES[status]) {
          return status;
        }

        if (status === PaymentStatementScheduleStatus.READY && new Date(endDate).getTime() < new Date().getTime()) {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontSize: '13px',
                width: '80px',
                padding: '4px 0 ',
                borderRadius: '4px',
                backgroundColor: '#444744',
                color: 'white',
              }}>
              완료
            </div>
          );
        }

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              fontSize: '13px',
              width: '80px',
              backgroundColor: SCHEDULE_STATUS_STYLES[status].backgroundColor,
              color: SCHEDULE_STATUS_STYLES[status].color,
              padding: '4px 0 ',
              borderRadius: '4px',
            }}>
            {SCHEDULE_STATUS_STYLES[status].label}
          </div>
        );
      },
    },
    {
      title: '환불상태',
      width: 100,
      dataIndex: ['refundStatus'],
      align: 'center',
      render: (refundStatus) => {
        return <div style={{ marginBottom: '3px' }}>{REFUND_STATUSES[refundStatus] || '-'}</div>;
      },
    },
    {
      title: '시작 시간',
      align: 'center',
      width: 100,
      dataIndex: ['schedule', 'startDate'],
      render: (startDate) => {
        return <div>{dayjs(startDate).format('YYYY.MM.DD HH:mm')}</div>;
      },
    },
    {
      title: '요일',
      width: '60px',
      align: 'center',
      dataIndex: ['schedule', 'week'],
      render: (week) => {
        const weekDays = ['일', '월', '화', '수', '목', '금', '토', '일'];
        return <div>{weekDays[week]}</div>;
      },
    },
    {
      title: '주문내역',
      align: 'center',
      width: 100,
      render: (_, record) => {
        return (
          <Button
            onClick={async () => {
              try {
                if (record.paymentStatement) {
                  setSelectedProductOrder(record.paymentStatement as ProductOrderViewModel);
                  setIsPaymentStatementModalOpen(true);
                } else if (record.salesChannelOrder) {
                  setSelectedProductOrder(record.salesChannelOrder as ProductOrderViewModel);
                  setIsPaymentStatementModalOpen(true);
                } else {
                  toast.error('주문내역이 없습니다');
                }
              } catch (error) {
                console.error('Error fetching payment statement:', error);
                toast.error('주문내역 조회 중 오류가 발생했습니다');
              }
            }}>
            보기
          </Button>
        );
      },
    },
    {
      title: '파트너명',
      align: 'center',
      width: 140,
      dataIndex: 'partnerName',
      render: (partnerName) => {
        return <div>{partnerName}</div>;
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>프로그램 일정 관리</div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <DatePicker
            value={dayjs(startDate)}
            onChange={(date) => {
              if (date) {
                setStartDate(date.toDate());
              }
            }}
            placeholder="시작일"
            style={{ width: '160px' }}
          />
          <DatePicker
            value={dayjs(endDate)}
            onChange={(date) => {
              if (date) {
                setEndDate(date.toDate());
              }
            }}
            placeholder="종료일"
            style={{ width: '160px' }}
          />
          <Button
            onClick={() => {
              setStartDate(dayjs().startOf('day').toDate());
              setEndDate(dayjs().endOf('day').toDate());
            }}>
            오늘
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().add(1, 'day').startOf('day').toDate());
              setEndDate(dayjs().add(1, 'day').endOf('day').toDate());
            }}>
            내일
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().startOf('week').toDate());
              setEndDate(dayjs().endOf('week').toDate());
            }}>
            이번주
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().add(1, 'week').startOf('week').toDate());
              setEndDate(dayjs().add(1, 'week').endOf('week').toDate());
            }}>
            다음주
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().startOf('month').toDate());
              setEndDate(dayjs().endOf('month').toDate());
            }}>
            이번달
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().add(1, 'month').startOf('month').toDate());
              setEndDate(dayjs().add(1, 'month').endOf('month').toDate());
            }}>
            다음달
          </Button>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <Input
              className={filterActiveClassName(!!searchKeyword)}
              ref={searchInputRef}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  programSchedulesQuery.refetch();
                }
              }}></Input>

            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  programSchedulesQuery.refetch();
                }}>
                검색 초기화
              </Button>
              <Button onClick={() => setIsScheduleCountModalVisible(true)}>기간내 옵션별 인원 수</Button>
              <Button
                onClick={async () => {
                  const fileName = `프로그램일정_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
                  const response = await api.exportProgramSchedules({ keyword: searchKeyword, startDate, endDate });
                  const blob = new Blob([response.data as unknown as BlobPart], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  });
                  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    // IE variant
                    (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
                  } else {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode && link.parentNode.removeChild(link);
                  }
                }}>
                엑셀 파일 다운로드
              </Button>
              <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
                외부 프로그램 일정 생성
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={'id'}
            expandable={{ showExpandColumn: false }}
            dataSource={programSchedulesQuery.data?.data.contents}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: programSchedulesQuery.data?.data.total,
              onChange: (page, newPageSize) => {
                setCurrentPage(page);
                setPageSize(newPageSize);
              },
            }}
            loading={programSchedulesQuery.isLoading}
          />
        </div>
      </div>

      <Modal
        title="외부 프로그램 일정 생성"
        open={isCreateModalVisible}
        onOk={handleCreate}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
          setSelectedProduct(undefined);
          setSelectedPartner(undefined);
        }}
        width={800}>
        <Form form={form} layout="vertical">
          <Form.Item label="파트너" required>
            <SelectUser
              selectedUserId={selectedPartner?._id}
              onSelect={(partner) => {
                setSelectedPartner(partner);
              }}
            />
          </Form.Item>

          <Form.Item label="상품" required>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Input
                value={selectedProduct?.title}
                placeholder="상품을 선택해주세요"
                readOnly
                style={{ width: '100%' }}
              />
              <Button onClick={() => setIsProductSelectModalVisible(true)}>상품 선택</Button>
            </div>
          </Form.Item>

          <Form.Item
            name="scheduleName"
            label="일정명(예: 5/25 / 13:00)"
            rules={[{ required: true, message: '일정명을 입력해주세요' }]}>
            <Input placeholder="일정명을 입력해주세요" />
          </Form.Item>

          <Form.Item
            name="duration"
            label="소요시간(분)"
            rules={[{ required: true, message: '소요시간을 입력해주세요' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="representativeName"
            label="대표자 이름"
            rules={[{ required: true, message: '대표자 이름을 입력해주세요' }]}>
            <Input placeholder="대표자 이름을 입력해주세요" />
          </Form.Item>

          <Form.Item
            name="representativePhone"
            label="대표자 전화번호"
            rules={[{ required: true, message: '대표자 전화번호를 입력해주세요' }]}>
            <Input placeholder="대표자 전화번호를 입력해주세요" />
          </Form.Item>
        </Form>
      </Modal>

      <SelectProductModal
        open={isProductSelectModalVisible}
        onClose={() => setIsProductSelectModalVisible(false)}
        onAddProducts={(products) => {
          setSelectedProduct(products[0]);
          setIsProductSelectModalVisible(false);
        }}
        selectLimit={1}
      />
      <PaymentStatementDisplayModal
        open={isPaymentStatementModalOpen}
        onClose={() => {
          setIsPaymentStatementModalOpen(false);
          setSelectedProductOrder(null);
        }}
        productOrder={selectedProductOrder}
      />
      <ScheduleCountByOptionModal
        open={isScheduleCountModalVisible}
        onClose={() => setIsScheduleCountModalVisible(false)}
        data={programSchedulesCountByOptionNameQuery.data?.data || []}
      />
    </div>
  );
};

export default ProgramSchedulesPage;
